import { CheckCircleFilled, LockOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, Space, Typography, message } from "antd";
import { useState } from "react";
import { type SetupPortal, useSubmitGroupAdminMutation } from "../../service/implementation.service";
import styles from "./setup.module.scss";

const { Text } = Typography;

type Props = {
  token: string;
  portal: SetupPortal;
  isComplete: boolean;
};

type Values = { name: string; email: string; phone?: string; password: string };

/**
 * Step 2 — designate the group administrator.
 *
 * Submitting creates their Nudge account with the password entered here and makes
 * them an admin of the group immediately, so their clinicians can start onboarding
 * while we build the EHR integration (issue §5). There is no invite to accept.
 *
 * Nothing submitted is ever rendered back — not the name, not the address. The page
 * is opened by a link practices forward around internally, and a password gets typed
 * into this form. That also means editing starts from a blank form.
 */
export default function GroupAdminForm({ token, isComplete }: Props) {
  const [submit, { isLoading }] = useSubmitGroupAdminMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (values: Values) => {
    setError(null);
    try {
      await submit({ token, ...values }).unwrap();
      message.success("Their Nudge account is ready.");
      setIsEditing(false);
    } catch (err) {
      const data = (err as { data?: { detail?: string; password?: string[] } })?.data;
      if (data?.password?.length) {
        setError(data.password.join(" "));
        return;
      }
      setError(data?.detail || "We couldn't save that. Please try again.");
    }
  };

  if (isComplete && !isEditing) {
    return (
      <>
        <Alert
          type="success"
          showIcon
          icon={<CheckCircleFilled />}
          message="Your group administrator is set up"
          description="Their Nudge account is ready and we've emailed them to say so. We don't show the details back here — set someone else up if you need to change it."
          action={
            <Button size="small" type="link" onClick={() => setIsEditing(true)}>
              Change
            </Button>
          }
        />
      </>
    );
  }

  return (
    <>
      <p className={styles.sectionLabel}>Who will administer Nudge for your practice?</p>

      {error ? <Alert type="warning" showIcon message={error} style={{ marginBottom: 16 }} /> : null}
      {isEditing ? (
        <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 12 }}>
          Setting up a different administrator starts from scratch — we don't show what was entered before. Whoever you
          set up previously keeps their access until you remove them in Member Management.
        </Text>
      ) : null}

      {/* Deliberately no initialValues: nothing submitted is echoed back, so an edit
          is a fresh entry rather than a prefilled one. */}
      <Form layout="vertical" onFinish={onSubmit} requiredMark={false}>
        <Form.Item
          label="Administrator's full name"
          name="name"
          rules={[{ required: true, message: "Please enter their name." }]}
        >
          <Input placeholder="Jane Smith" autoComplete="off" />
        </Form.Item>
        <Form.Item
          label="Administrator's email"
          name="email"
          extra="This becomes their Nudge username."
          rules={[
            { required: true, message: "Please enter their email." },
            { type: "email", message: "That doesn't look like a valid email address." },
          ]}
        >
          <Input placeholder="jane@yourpractice.com" autoComplete="off" inputMode="email" />
        </Form.Item>
        <Form.Item
          label="Set their password"
          name="password"
          extra="We'll create their Nudge account with this. Pass it on to them directly — we won't email it."
          rules={[{ required: true, message: "Please set a password for their account." }]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item label="Phone (optional)" name="phone">
          <Input placeholder="(555) 010-4477" autoComplete="off" />
        </Form.Item>

        <Space>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isComplete ? "Set up this administrator" : "Create their account"}
          </Button>
          {isEditing ? <Button onClick={() => setIsEditing(false)}>Cancel</Button> : null}
        </Space>
      </Form>

      <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 14 }}>
        <LockOutlined /> Pick someone who will actually be in the system day to day — they'll be inviting clinicians,
        revoking access when someone leaves, and managing your shared templates.
      </Text>
    </>
  );
}
